import { useLayoutEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ExpandableSection from '../../common/ExpandableSection'
import VideoModal from '../../common/VideoModal'
import WrappedTabs from '../../common/WrappedTabs'
import SimpleTextInput from '../../common/SimpleTextInput'
import Dropdown from '../../common/Dropdown'
import ConfirmationModal from '../../common/ConfirmationModal'
import TooltipedIcon from '../../common/TooltipedIcon'
import ToggleSwitch from '../../common/ToggleSwitch'
import { EditIcon, InformationIcon, SuccessTick, TrashCanFull, VideoPlayIcon } from '../../../icons'
import { troubleshootingUploadWritePermission, troubleshootingUploadDeletePermission } from '../../../utils/permissionValidation'
import { capitalizeFirstLetter } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import { checkPermission } from '../../../utils/permissionValidation'
import { keyboardEvents } from '../../../utils/keyboardEvents'
import { createFaultDefinition, removeFaultDefinition, updateFaultDefinition } from '../../../actions/troubleshooting'
import '../uploads.scss'

const ViewFaultModal = (props) => {
  const { t } = useTranslation()
  const englishLanguageCode = 'EN'
  const techcianTabId = 'technician'
  const userTabId = 'user'
  const { faultDetails, handleEditFault, closeModal } = props

  const [expandedItem, setExpandedItem] = useState(null)
  const [editingLanguageCode, setEditingLanguageCode] = useState(null)
  const [originalLanguages, setOriginalLanguages] = useState([])
  const [languages, setLanguages] = useState([])
  const [selectedLanguageTab, setSelectedLanguageTab] = useState(techcianTabId)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [showDiscardChangesConfirmModal, setShowDiscardChangesConfirmModal] = useState(false)
  const [pendingTab, setPendingTab] = useState(null)
  const [pendingExpandIndex, setPendingExpandIndex] = useState(null)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [definitionToDelete, setDefinitionToDelete] = useState(null)
  const hasEditPermission = checkPermission(props.userPermissions, troubleshootingUploadWritePermission)
  const hasDeletePermission = checkPermission(props.userPermissions, troubleshootingUploadDeletePermission)
  const Statuses = ['inactive', 'active']

  useLayoutEffect(() => {
    if (!faultDetails?.definitions) return

    const techUserGroupId = 1
    const userUserGroupId = 2

    const definitionLanguages = [...new Set(faultDetails.definitions.map((definition) => definition.languageCode))]

    const mappedLanguages = definitionLanguages.map((lang) => {
      const langDefinitions = faultDetails.definitions.filter((definition) => definition.languageCode === lang)

      const techDef = langDefinitions.find((d) => d.userGroupId === techUserGroupId)
      const userDef = langDefinitions.find((d) => d.userGroupId === userUserGroupId)

      return {
        code: lang,
        technician: {
          descriptions: techDef?.descriptions?.length ? techDef.descriptions.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
          probableCauses: techDef?.probableCause?.length ? techDef.probableCause.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
          techTroubleshootings: techDef?.techTroubleshooting?.length
            ? techDef.techTroubleshooting.map((v) => ({ id: uuidv4(), value: v }))
            : [{ id: uuidv4(), value: '' }],
          userTroubleshootings: techDef?.userTroubleshooting?.length
            ? techDef.userTroubleshooting.map((v) => ({ id: uuidv4(), value: v }))
            : [{ id: uuidv4(), value: '' }],
          id: techDef?.id || null,
        },
        user: {
          descriptions: userDef?.descriptions?.length ? userDef.descriptions.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
          probableCauses: userDef?.probableCause?.length ? userDef.probableCause.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
          userTroubleshootings: userDef?.userTroubleshooting?.length
            ? userDef.userTroubleshooting.map((v) => ({ id: uuidv4(), value: v }))
            : [{ id: uuidv4(), value: '' }],
          id: userDef?.id || null,
        },
      }
    })

    setLanguages(mappedLanguages)
    setOriginalLanguages(JSON.parse(JSON.stringify(mappedLanguages)))
  }, [faultDetails])

  const handleExpandSection = (index) => {
    if (hasUnsavedChanges()) {
      setPendingExpandIndex(index)
      setShowDiscardChangesConfirmModal(true)
      return
    }

    setExpandedItem((prev) => (prev === index ? null : index))
  }

  const hasUnsavedChanges = () => {
    if (editingLanguageCode === null) return false
    const edited = languages[editingLanguageCode]
    const original = originalLanguages[editingLanguageCode]
    return JSON.stringify(edited) !== JSON.stringify(original)
  }

  const updateLanguageField = (code, field, id, value) => {
    setLanguages((prev) =>
      prev.map((lang) =>
        lang.code === code
          ? {
              ...lang,
              [selectedLanguageTab]: {
                ...lang[selectedLanguageTab],
                [field]: lang[selectedLanguageTab][field].map((item) => (item.id === id ? { ...item, value } : item)),
              },
            }
          : lang,
      ),
    )
  }

  const updateLanguageCode = (oldCode, newCode) => {
    setLanguages((prev) =>
      prev.map((lang) => {
        if (lang.code === oldCode) {
          return {
            ...lang,
            code: newCode,
          }
        }
        return lang
      }),
    )
  }

  const addLanguageField = (code, field) => {
    setLanguages((prev) =>
      prev.map((lang) =>
        lang.code === code
          ? {
              ...lang,
              [selectedLanguageTab]: {
                ...lang[selectedLanguageTab],
                [field]: [...lang[selectedLanguageTab][field], { id: uuidv4(), value: '' }],
              },
            }
          : lang,
      ),
    )
  }

  const removeLanguageField = (code, field, id) => {
    setLanguages((prev) =>
      prev.map((lang) => {
        if (lang.code === code) {
          const filtered = lang[selectedLanguageTab][field].filter((item) => item.id !== id)
          return {
            ...lang,
            [selectedLanguageTab]: {
              ...lang[selectedLanguageTab],
              [field]: filtered.length ? filtered : [{ id: uuidv4(), value: '' }],
            },
          }
        }
        return lang
      }),
    )
  }

  const handleCancelEdit = () => {
    setLanguages(JSON.parse(JSON.stringify(originalLanguages)))
    setEditingLanguageCode(null)
  }

  const updateLocalLanguage = (role, langCode, payload) => {
    const updatedLang = {
      code: langCode,
      [role]: {
        descriptions: payload.descriptions?.length > 0 ? payload.descriptions.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
        probableCauses: payload.probableCause?.length > 0 ? payload.probableCause.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
        techTroubleshootings:
          payload.techTroubleshooting?.length > 0 ? payload.techTroubleshooting.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
        userTroubleshootings:
          payload.userTroubleshooting?.length > 0 ? payload.userTroubleshooting.map((v) => ({ id: uuidv4(), value: v })) : [{ id: uuidv4(), value: '' }],
        id: payload.id || null,
      },
    }

    const newLanguages = languages.map((lang) => (lang.code === langCode ? { ...lang, [role]: updatedLang[role] } : lang))

    setLanguages(newLanguages)
    setOriginalLanguages(JSON.parse(JSON.stringify(newLanguages)))
    setEditingLanguageCode(null)
  }

  const handleSaveChanges = async () => {
    const role = selectedLanguageTab

    const language = languages[editingLanguageCode]
    const langCode = language.code
    const faultId = faultDetails.id
    const userGroupId = role === 'technician' ? 1 : 2

    const current = language[role]

    const descriptions = current.descriptions.map((d) => d.value.trim()).filter(Boolean)
    const probableCauses = current.probableCauses.map((p) => p.value.trim()).filter(Boolean)

    const techTroubleshooting = current.techTroubleshootings?.map((t) => t.value.trim()).filter(Boolean) || []
    const userTroubleshooting = current.userTroubleshootings?.map((t) => t.value.trim()).filter(Boolean) || []

    const payload = {
      userGroupId,
      languageCode: langCode,
      descriptions,
      probableCause: probableCauses,
      ...(role === 'technician'
        ? {
            techTroubleshooting,
            userTroubleshooting,
          }
        : {
            userTroubleshooting,
          }),
    }

    try {
      const definitionId = languages[editingLanguageCode]?.[role]?.id

      if (definitionId) {
        await props.actions.updateFaultDefinition(faultId, definitionId, payload)
        updateLocalLanguage(role, langCode, { ...payload, id: definitionId })
        return
      }

      const responseAction = await props.actions.createFaultDefinition(faultId, payload)
      const newDefinition = responseAction?.value

      updateLocalLanguage(role, langCode, {
        ...payload,
        id: newDefinition.definitionId,
      })
    } catch (error) {
      console.error('Failed to update fault definition:', error)
    }
  }

  const troubleshootingHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.status_toggle_text')}`} orientation="bottom" />
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={faultDetails.isActive} isDisabled={true} />
        <div className="status">{t(Statuses[Number(faultDetails.isActive)])}</div>
      </div>
    )
  }

  const renderLanguageSection = (language, index) => {
    const isEditing = editingLanguageCode === index
    return (
      <div className="row-wrapper" key={language.code}>
        <ExpandableSection
          title={`${t(languagesShort[language.code.toLowerCase()] || language.code)} (${language.code})`}
          isExpanded={expandedItem === index}
          onToggle={() => handleExpandSection(index)}
          className="with-content-expandable large-body-spacing"
          additionalContent={
            <div className="expandable-action">
              {hasEditPermission && !isEditing && expandedItem === index && (
                <EditIcon
                  className="with-margin-left action-icon"
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditingLanguageCode(index)
                  }}
                />
              )}
              {hasDeletePermission && !isEditing && expandedItem === index && language.code !== 'EN' && (
                <TrashCanFull
                  className="action-icon"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDefinitionToDelete({ code: language.code, techId: language.technician?.id, userId: language.user?.id })
                    setShowDeleteConfirmModal(true)
                  }}
                />
              )}
              {isEditing && (
                <div className="input-wrapper max-content-width flex-row align-center">
                  {selectedLanguageTab === userTabId && (
                    <span
                      className="action-label submit-label"
                      onClick={(e) => {
                        e.stopPropagation()
                        const tech = language.technician
                        const userCopy = {
                          descriptions: tech.descriptions.map((v) => ({ id: uuidv4(), value: v.value })),
                          probableCauses: tech.probableCauses.map((v) => ({ id: uuidv4(), value: v.value })),
                          userTroubleshootings: tech.userTroubleshootings.map((v) => ({ id: uuidv4(), value: v.value })),
                        }

                        setLanguages((prev) =>
                          prev.map((lang) =>
                            lang.code === language.code
                              ? {
                                  ...lang,
                                  user: {
                                    ...lang.user,
                                    ...userCopy,
                                  },
                                }
                              : lang,
                          ),
                        )
                      }}
                    >
                      {t('copy_from_tech')}
                    </span>
                  )}
                  <span
                    className="with-margin-left action-label"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCancelEdit()
                    }}
                  >
                    {t('cancel')}
                  </span>
                  <span
                    className="action-label submit-label"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSaveChanges()
                    }}
                  >
                    {t('save')}
                  </span>
                </div>
              )}
            </div>
          }
        >
          <div className="row-wrapper with-sides-no-padding">
            <div className="input-wrapper">
              <p className="input-option">{`${t('language')}*`}</p>
              {isEditing ? (
                <Dropdown
                  placeholder={t('please_select')}
                  options={props.languageOptions.map((lang) => ({
                    key: lang,
                    value: `${t(languagesShort[lang.toLowerCase()] || lang)}`,
                  }))}
                  initialSelectedKey={language.code}
                  onChange={(lang) => updateLanguageCode(language.code, lang.key)}
                  isDisabled={true}
                />
              ) : (
                <div className="value">{`${t(languagesShort[language.code.toLowerCase()] || language.code)} (${language.code})`}</div>
              )}
            </div>
            <div className="input-wrapper empty" />
          </div>
          {['descriptions', 'probableCauses'].map((field) => (
            <div className="row-wrapper with-sides-no-padding" key={`${language.code}-${field}`}>
              <div className="input-wrapper">
                <p className="input-option">{`${t(field === 'descriptions' ? 'description' : 'troubleshoot.probable-cause')}`}</p>
                {isEditing ? (
                  <>
                    {language[selectedLanguageTab][field].map((item) => (
                      <div className="d-flex align-center" key={item.id}>
                        <SimpleTextInput
                          value={item.value}
                          onChange={(val) => updateLanguageField(language.code, field, item.id, val)}
                          onClear={() => updateLanguageField(language.code, field, item.id, '')}
                        />
                        <TrashCanFull className="with-margin-left action-icon" onClick={() => removeLanguageField(language.code, field, item.id)} />
                      </div>
                    ))}
                    <button className="add-btn" onClick={() => addLanguageField(language.code, field)}>
                      + {field === 'descriptions' ? t('add_new_description') : t('troubleshoot.add_probable_cause')}
                    </button>
                  </>
                ) : (
                  <ul className="list-none">
                    {language[selectedLanguageTab][field]
                      .filter((item) => item.value?.trim())
                      .map((item) => (
                        <li key={item.id}>{item.value}</li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          ))}

          {(selectedLanguageTab === techcianTabId ? ['techTroubleshootings', 'userTroubleshootings'] : ['userTroubleshootings']).map((field) => (
            <div className="row-wrapper with-sides-no-padding" key={`${language.code}-${field}`}>
              <div className="input-wrapper">
                <p className="input-option">{`${t(field === 'techTroubleshootings' ? 'troubleshoot.tech' : 'troubleshoot.user-troubleshoot')}`}</p>
                {isEditing ? (
                  <>
                    {language[selectedLanguageTab][field].map((item) => (
                      <div className="d-flex align-center" key={item.id}>
                        <SimpleTextInput
                          value={item.value}
                          onChange={(val) => updateLanguageField(language.code, field, item.id, val)}
                          onClear={() => updateLanguageField(language.code, field, item.id, '')}
                        />
                        <TrashCanFull className="with-margin-left action-icon" onClick={() => removeLanguageField(language.code, field, item.id)} />
                      </div>
                    ))}
                    <button className="add-btn" onClick={() => addLanguageField(language.code, field)}>
                      + {field === 'techTroubleshootings' ? t('troubleshoot.add_tech_troubleshoot') : t('troubleshoot.add_user_troubleshoot')}
                    </button>
                  </>
                ) : (
                  <ul className="list-none">
                    {language[selectedLanguageTab][field]
                      .filter((item) => item.value?.trim())
                      .map((item) => (
                        <li key={item.id}>{item.value}</li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </ExpandableSection>
      </div>
    )
  }

  return (
    <PageModal
      toggle
      onToggle={closeModal}
      className="uploads-modal"
      title={t('details')}
      headerContent={troubleshootingHeaderContent()}
      footerContent={
        <>
          <SimpleButton className="cancel-button" onClick={closeModal}>
            {t('close')}
          </SimpleButton>
          <SimpleButton className="action-button" onClick={handleEditFault} requiredPermission={troubleshootingUploadWritePermission}>
            {t('edit')}
          </SimpleButton>
        </>
      }
    >
      <div className="uploads-modal-content">
        <div className="section-title">{t('troubleshoot.fault_setup').toUpperCase()}</div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{capitalizeFirstLetter(t('models'))}</p>
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {faultDetails.models?.map((model, index) => (
                  <div className="value" key={index}>
                    <SuccessTick />
                    {model}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="inner-separator" />
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option flex-justify-start">
              {t('troubleshoot.fault_name')}
              <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.fault_name_info')}`} orientation="extra-right" />
            </p>
            <div className="value">{faultDetails.fault}</div>
          </div>
        </div>
        <div className="separator" />
        <div className="section-title d-flex flex-justify-start flex-align-center">
          {t('languages').toUpperCase()}
          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.tabs_info')}`} orientation="extra-right" />
        </div>
        <div className="row-wrapper">
          <WrappedTabs
            containerId="languages-wrapped-tabs"
            tabs={[
              { id: techcianTabId, name: 'technician' },
              { id: userTabId, name: 'end_user' },
            ]}
            activeTab={selectedLanguageTab}
            changeTab={(tab) => {
              if (hasUnsavedChanges()) {
                setPendingTab(tab)
                setShowDiscardChangesConfirmModal(true)
              } else {
                setSelectedLanguageTab(tab)
              }
            }}
          />
        </div>
        {languages.map((language, index) => renderLanguageSection(language, index))}
        <div className="section-title">{t('videos').toUpperCase()}</div>
        <div className="row-wrapper">
          <div className="videos-wrapper">
            {faultDetails?.videos.map((video, index) => (
              <div
                key={index}
                className="video-item-wrapper"
                onClick={() => setSelectedVideo(video)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => e.key === keyboardEvents.ENTER && setSelectedVideo(video)}
              >
                <div className="thumbnail-wrapper">
                  <div className="thumbnail-icon">
                    <VideoPlayIcon />
                  </div>
                  <img src={video.thumbnailUrl} alt="video" />
                </div>
                <p className="title">{video.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {selectedVideo && <VideoModal title={selectedVideo.title} videoId={selectedVideo.videoId} onCloseVideo={() => setSelectedVideo(null)} />}
      {showDiscardChangesConfirmModal && (
        <ConfirmationModal
          onToggle={() => {
            setShowDiscardChangesConfirmModal(false)
            setPendingTab(null)
            setPendingExpandIndex(null)
          }}
          onAccept={() => {
            handleCancelEdit()
            setShowDiscardChangesConfirmModal(false)
            if (pendingTab !== null) {
              setSelectedLanguageTab(pendingTab)
              setPendingTab(null)
            }
            if (pendingExpandIndex !== null) {
              setExpandedItem((prev) => (prev === pendingExpandIndex ? null : pendingExpandIndex))
              setPendingExpandIndex(null)
            }
          }}
          onCancel={() => {
            setShowDiscardChangesConfirmModal(false)
            setPendingTab(null)
            setPendingExpandIndex(null)
          }}
          message={t('discard_confirmation')}
        />
      )}
      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => {
            setShowDeleteConfirmModal(false)
            setDefinitionToDelete(null)
          }}
          onAccept={async () => {
            const { techId, userId, code } = definitionToDelete
            const faultId = faultDetails.id

            if (userId) {
              await props.actions.removeFaultDefinition(faultId, userId)
            }

            if (techId) {
              await props.actions.removeFaultDefinition(faultId, techId)
            }

            setLanguages((prev) => prev.filter((l) => l.code !== code))
            setOriginalLanguages((prev) => prev.filter((l) => l.code !== code))

            setShowDeleteConfirmModal(false)
            setDefinitionToDelete(null)
          }}
          onCancel={() => {
            setShowDeleteConfirmModal(false)
            setDefinitionToDelete(null)
          }}
          message={t('troubleshoot.confirm_delete_definition')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={troubleshootingUploadDeletePermission}
        />
      )}
    </PageModal>
  )
}

function stateToProps({ shared, troubleshooting, userPermissions }) {
  return {
    languageOptions: shared?.languages?.map((l) => l.languageCode) || [],
    faultDetails: troubleshooting?.faultDetails || {},
    userPermissions,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createFaultDefinition,
        updateFaultDefinition,
        removeFaultDefinition,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ViewFaultModal)

